import { CartAddEvent } from '@theme/events';
import { track, variantOf } from '@theme/hds-experiments';
import { getCart } from '@theme/cart-state';

/**
 * Document-delegatie voor PDP-blokken zónder custom element (M4 uit docs/PDP-BEVINDINGEN.md).
 *
 * Waarom: bij een combined-listing-switch morpht het thema de hele <main>. Inline
 * <script>-blokken in blocks/snippets draaien daarna nooit (opnieuw) — hergebruikte
 * nodes houden verouderde closures, nieuw ingevoegde nodes krijgen nooit een listener.
 * Delegatie vanuit dit altijd-geladen asset is daar immuun voor, óók voor blokken die
 * pas ná een switch verschijnen. Regels: state altijd op klik-moment uit de DOM lezen
 * (dataset/attributen), nooit uit closures.
 *
 * Dekt:
 * - Meetverzekering-chip (blocks/product-meetverzekering.liquid)
 * - Staal-add-to-cart-knop (snippets/button.liquid)
 * - Het chip-deel van de A/B-test op de meetverzekering (SHO-10062)
 */

/**
 * Voegt het staal van de knop toe aan de cart. Leest `data-variant-id` op klik-moment,
 * zodat na een combined-listing-morph altijd het staal van het huidige product wordt
 * toegevoegd (de oude inline-closure hield de variant van het vorige product vast).
 *
 * @param {HTMLButtonElement} button
 */
async function addStaalToCart(button) {
  if (button.disabled || button.hasAttribute('data-loading')) return;

  const variantId = Number.parseInt(button.dataset.variantId ?? '', 10);
  if (!Number.isFinite(variantId)) return;

  button.disabled = true;
  button.setAttribute('data-loading', 'true');

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to add item to cart');
    }

    const cart = await getCart({ vers: true });

    document.dispatchEvent(
      new CartAddEvent(cart, `product-button-${button.dataset.blockId}`, {
        source: 'product-button',
        itemCount: cart.item_count,
        variantId: String(variantId),
      })
    );

    button.removeAttribute('data-loading');
    button.setAttribute('data-added', 'true');

    // Reset na de animatie (zelfde duur als de reguliere add-to-cart-knop)
    setTimeout(() => {
      button.removeAttribute('data-added');
      button.disabled = false;
    }, 3000);
  } catch (error) {
    console.error('Error adding to cart:', error);
    button.removeAttribute('data-loading');
    button.disabled = false;
  }
}

/** @param {Element} chip */
function toggleMeetverzekering(chip) {
  const panelId = chip.getAttribute('aria-controls');
  const panel = panelId ? document.getElementById(panelId) : null;
  if (!panel) return;

  const open = chip.getAttribute('aria-expanded') === 'true';
  chip.setAttribute('aria-expanded', String(!open));
  panel.hidden = open;

  // Alleen het openen meten. Deze chip is in beide armen dezelfde, dus dit event is
  // de gedeelde nulmeting; de strip in de prijskaart stuurt hetzelfde event met
  // placement 'price_box'. Samen beantwoorden ze: hoe vaak wil iemand die uitleg
  // lezen, en verschuift dat naar de prijskaart zodra hij daar ook staat?
  if (open) return;
  const experiment = chip.getAttribute('data-mv-experiment');
  track('hds:mv_open', {
    placement: 'chip',
    experiment_id: experiment ?? '',
    variant: experiment ? variantOf(experiment) : '',
    product_id: productIdNear(chip),
  });
}

/**
 * Product-id van de PDP waar deze chip bij hoort. De configurator zet het op zijn
 * eigen sectie; staat die er niet, dan blijft het veld leeg — het event is dan nog
 * steeds bruikbaar, alleen niet uit te splitsen per product.
 *
 * @param {Element} el
 * @returns {string}
 */
function productIdNear(el) {
  const section = document.querySelector('.product-configurator-section[data-product-id]');
  return section?.getAttribute('data-product-id') ?? el.closest('[data-product-id]')?.getAttribute('data-product-id') ?? '';
}


// Capture-fase: de oude inline staal-handler deed stopPropagation op de knop zelf;
// alleen vanuit capture kunnen we dat gedrag (geen andere handlers) behouden.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const chip = target.closest('.mv__chip[aria-controls]');
    if (chip) {
      toggleMeetverzekering(chip);
      return;
    }

    const staalButton = target.closest('button.product-button-add-to-cart[data-variant-id]');
    if (staalButton instanceof HTMLButtonElement) {
      // Heeft het staal meerdere formaten, dan is staal-formaatkiezer.js aan zet: die opent
      // de sheet en heeft dit event in dezelfde capture-fase al gestopt. De guard staat er
      // voor het geval de registratievolgorde ooit omdraait — dan mag deze handler niet
      // alsnog stilletjes de Standaard-maat toevoegen. Op de definitie van het element
      // checken en niet op het attribuut alleen: laadde dat script niet, dan moet deze knop
      // gewoon blijven werken zoals altijd. (SHO-9835)
      if (staalButton.dataset.staalVarianten && customElements.get('staal-formaatkiezer')) return;

      event.preventDefault();
      event.stopPropagation();
      addStaalToCart(staalButton);
    }
  },
  { capture: true }
);
